import React from 'react';

import { FixMeLater } from '../../utils';
import { ColorOption, IconSize, useComponentDefaultProps, useHoneycombTheme } from '../../theming';
import { Styler, StylerProps } from '../Styler';

export interface SpinnerProps extends StylerProps {
    size: number | string;
    color: string;
}

export function Spinner(props: Readonly<SpinnerProps>): React.JSX.Element {
    const { size, color, ref, ...other } = props;

    return (
        <Styler
            ref={ref}
            component="svg"
            w={size}
            h={size}
            viewBox="0 0 38 38"
            xmlns="http://www.w3.org/2000/svg"
            stroke={color}
            {...other}
        >
            <g fill="none" fillRule="evenodd">
                <g transform="translate(2.5 2.5)" strokeWidth="5">
                    <circle strokeOpacity=".5" cx="16" cy="16" r="16" />
                    <path d="M32 16c0-9.94-8.06-16-16-16">
                        <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 16 16"
                            to="360 16 16"
                            dur="1s"
                            repeatCount="indefinite"
                        />
                    </path>
                </g>
            </g>
        </Styler>
    );
}

export interface LoaderProps extends StylerProps {
    color?: ColorOption;

    /**
     * The size of the loader.
     * @default m
     */
    size?: IconSize;
}

const defaultProps: Partial<LoaderProps> = {
    size: 'm',
};

export const Loader: React.ForwardRefExoticComponent<LoaderProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Loader(props: LoaderProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const { size = 'm', color, ...other } = useComponentDefaultProps('Loader', defaultProps, props);

        const { theme } = useHoneycombTheme();
        const colorValue = color ? theme.fn.getColor(color) : 'currentColor';

        return (
            <Spinner ref={ref} role="presentation" size={`var(--icon-size-${size})`} color={colorValue} {...other} />
        );
    }
);
